import { useState } from 'react';
import { uploadFileToServer } from '../../utils/server-upload';
import { useMutation } from './useMutation';
import type { Post, ExportData } from '../types';

interface UseEmailSendingProps {
  posts: Post[];
  primaryMonth: string;
}

interface SendEmailParams {
  pendingEmailPdf: ExportData;
  emailAddress: string;
}

export function useEmailSending({ posts, primaryMonth }: UseEmailSendingProps) {
  const [directEmailDialogOpen, setDirectEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [pendingEmailPdf, setPendingEmailPdf] = useState<ExportData | null>(null);

  const sendEmailMutation = useMutation<string, Error, SendEmailParams>({
    mutationFn: async ({ pendingEmailPdf, emailAddress }) => {
      const serverUrl = import.meta.env.VITE_MCP_SERVER_URL || 'http://localhost:8000';

      let mimeType = 'application/pdf';
      if (pendingEmailPdf.filename.endsWith('.png')) {
        mimeType = 'image/png';
      } else if (pendingEmailPdf.filename.endsWith('.csv')) {
        mimeType = 'text/csv';
      }

      const { url: fileUrl } = await uploadFileToServer({
        dataUrl: pendingEmailPdf.dataUrl,
        mimeType: mimeType,
        filename: pendingEmailPdf.filename,
      });

      const platformCount = new Set(posts.map(p => p.platform)).size;

      const response = await fetch(`${serverUrl}/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailAddress,
          fileUrl: fileUrl,
          month: primaryMonth,
          postCount: posts.length,
          platformCount: platformCount,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send email' }));
        throw new Error(errorData.error || 'Failed to send email');
      }

      return 'Email sent successfully!';
    },
    onSuccess: () => {
      setDirectEmailDialogOpen(false);
      setEmailAddress('');
      setPendingEmailPdf(null);
    },
    onError: (error) => {
      console.error('Failed to send email:', error);
    },
  });

  const handleConfirmSendEmail = async (): Promise<string | undefined> => {
    if (!pendingEmailPdf || !emailAddress) return undefined;
    return await sendEmailMutation.mutateAsync({ pendingEmailPdf, emailAddress });
  };

  return {
    directEmailDialogOpen,
    setDirectEmailDialogOpen,
    emailAddress,
    setEmailAddress,
    isSendingEmail: sendEmailMutation.isLoading,
    emailSendError: sendEmailMutation.error?.message || null,
    setEmailSendError: () => sendEmailMutation.reset(),
    pendingEmailPdf,
    setPendingEmailPdf,
    handleConfirmSendEmail,
  };
}
